import React from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShallow } from "zustand/react/shallow";
import { getSoftwareAccent, getVisibleSettingsSections, type SettingsSectionDescriptor } from "@domain/software";
import type { SoftwareEnumSchema, SoftwareSettingsMetadata } from "@domain/schemas/BI";
import type { MergeNodePoolOption } from "@domain/valueObjects";
import { licensedSoftwareOrNull, shouldQualifyWithEngineName, useLicensedSoftware } from "./useLicensedSoftware";
import { useRightPanelTabIndex } from "./useRightPanelTabIndex";

type MergeNodePoolDropdownItem = {
  value: string | null;
  title: string;
  summary?: string;
  option?: MergeNodePoolOption;
};

// Icons stay in the presentation layer; the profile only describes the sections.
const SETTINGS_SECTION_ICONS: Record<string, typeof svgIcons.fileReportIcon> = {
  Experiment: svgIcons.chartColumnStacked100Icon,
  Report: svgIcons.fileReportIcon,
  GO: svgIcons.clipboardCodeIcon,
  Modification: svgIcons.parameterBooleanIcon,
  ConditionSetup: svgIcons.clipboardIcon,
  SpectroMineSearch: svgIcons.chartColumnStacked100Icon,
};

/**
 * States the engine scope once for the whole settings area, instead of repeating
 * it above every picker. The colour matches the outlines of the engine-scoped
 * pickers below, so the banner is the legend for that colour.
 */
const SettingsScopeBanner: React.FC<{ software?: SoftwareEnumSchema | null }> = ({ software }) => {
  // eslint-disable-next-line unicorn/no-declarations-before-early-exit -- hooks must run before any early return
  const licensedSoftware = licensedSoftwareOrNull(useLicensedSoftware());
  if (!software) return null;
  // The banner only earns its space when the engine scope is actually ambiguous,
  // i.e. the license state has not resolved yet or does not resolve to one engine.
  if (!shouldQualifyWithEngineName(licensedSoftware)) return null;
  const accent = getSoftwareAccent(software);

  return (
    <div
      role="note"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        margin: "4px 0 12px",
        padding: "8px 10px",
        borderRadius: "6px",
        border: `1px solid ${accent.border}`,
        borderLeft: `4px solid ${accent.border}`,
        backgroundColor: accent.background,
        color: accent.text,
        fontSize: "0.85em",
      }}
    >
      <SvgIcon icon={svgIcons.infoCircleIcon} />
      <span>
        Showing <strong>{software}</strong> settings only. Neutral outlines are shared across search engines.
      </span>
    </div>
  );
};

const SettingsSection: React.FC<{ section: SettingsSectionDescriptor; isAdvanced?: boolean }> = ({ section, isAdvanced = false }) => {
  const setRightPanelTab = useJobSetupStore((state) => state.setRightPanelTab);
  const tabIndex = useRightPanelTabIndex(section.settingsType, isAdvanced);
  const selected = useJobSetupStore(
    useShallow((state) => {
      switch (section.settingsType) {
        case "Experiment": {
          return state.settingsDirectDIA ? [state.settingsDirectDIA] : [];
        }
        case "Report": {
          return state.reportSettings ?? [];
        }
        case "GO": {
          return state.goSettings ?? [];
        }
        case "Modification": {
          return state.modificationRepository ? [state.modificationRepository] : [];
        }
        case "ConditionSetup": {
          return state.conditionSetup ? [state.conditionSetup] : [];
        }
        case "SpectroMineSearch": {
          return state.settingsSpectroMineSearch ? [state.settingsSpectroMineSearch] : [];
        }
        default: {
          return [] as SoftwareSettingsMetadata[];
        }
      }
    }),
  );
  const hasSelection = selected.length > 0;

  return (
    <fieldset className={"k-form-fieldset"}>
      <legend className={"k-form-legend"}>
        {section.tooltip ? (
          <span title={section.tooltip} style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "help" }}>
            <strong>{section.label}</strong>
            <SvgIcon style={{ paddingBottom: "5px" }} icon={SETTINGS_SECTION_ICONS[section.settingsType]} />
          </span>
        ) : (
          <>
            <strong>{section.label}</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={SETTINGS_SECTION_ICONS[section.settingsType]} />
          </>
        )}
      </legend>
      <div
        onClick={() => setRightPanelTab(tabIndex)}
        style={{
          border: "1px solid #d1d5db",
          borderRadius: "3px",
          padding: "8px",
          minHeight: "32px",
          backgroundColor: "white",
          cursor: "pointer",
        }}
      >
        {hasSelection ? (
          selected.map((item) => (
            <span
              key={item.id}
              style={{ display: "inline-block", backgroundColor: "#e3f2fd", border: "1px solid #2196f3", borderRadius: "3px", padding: "2px 8px", fontSize: "12px", color: "#1976d2", margin: "2px" }}
            >
              {item.name}
            </span>
          ))
        ) : (
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>
            No {section.label.toLowerCase()} selected — click here or switch to the <strong>{section.label}</strong> tab on the right.
          </span>
        )}
      </div>
    </fieldset>
  );
};

const formatMergeNodePoolSummary = (option: MergeNodePoolOption) => {
  const storage = option.storageBuffer ? ` / ${option.storageBuffer} storage` : "";
  const sneCombine = option.useSNECombine ? ", Uses SNE Combine" : "";
  return `${option.cpu} CPU / ${option.memory}${storage}${sneCombine}`;
};

/**
 * The engine-specific settings pickers, rendered directly under the software and
 * version selection so the choices that depend on the engine stay together.
 */
export const SoftwareSettingsFormBlock: React.FC = () => {
  const CreateJobState = useJobSetupStore((state) => state.CreateJobState);
  const sections = getVisibleSettingsSections(CreateJobState.software, CreateJobState.pipelineType, false);

  return (
    <>
      <SettingsScopeBanner software={CreateJobState.software} />
      {sections.map((section) => (
        <SettingsSection key={section.settingsType} section={section} />
      ))}
    </>
  );
};

export const JobSettingsFormBlock: React.FC<{
  isAdvancedSettingsExpanded: boolean;
  onAdvancedSettingsExpandedChange: (isExpanded: boolean) => void;
}> = ({ isAdvancedSettingsExpanded, onAdvancedSettingsExpandedChange }) => {
  const { CreateJobState, mergeNodePoolOptions, mergeNodePoolOptionsLoading, mergeNodePoolOptionsError, setMergeNodePoolOverride } = useJobSetupStore(
    useShallow((state) => ({
      CreateJobState: state.CreateJobState,
      mergeNodePoolOptions: state.mergeNodePoolOptions,
      mergeNodePoolOptionsLoading: state.mergeNodePoolOptionsLoading,
      mergeNodePoolOptionsError: state.mergeNodePoolOptionsError,
      setMergeNodePoolOverride: state.setMergeNodePoolOverride,
    })),
  );

  const mergeNodePoolItems: MergeNodePoolDropdownItem[] = [
    { title: "Use default merge pool", value: null },
    ...mergeNodePoolOptions.map((option) => ({
      title: option.profile,
      summary: `${option.nodePoolName} · ${formatMergeNodePoolSummary(option)}`,
      value: option.profileKey,
      option,
    })),
  ];
  const hasKnownSelectedProfile = mergeNodePoolOptions.some((option) => option.profileKey === CreateJobState.mergeNodePoolOverride);
  const selectedMergeNodePool =
    mergeNodePoolItems.find((item) => item.value === (CreateJobState.mergeNodePoolOverride ?? null)) ??
    (!hasKnownSelectedProfile && CreateJobState.mergeNodePoolOverride
      ? {
          title: "Legacy node pool override",
          summary: CreateJobState.mergeNodePoolOverride,
          value: CreateJobState.mergeNodePoolOverride,
        }
      : mergeNodePoolItems[0]);
  const selectedMergeNodePoolOption = mergeNodePoolOptions.find((option) => option.profileKey === CreateJobState.mergeNodePoolOverride) ?? null;

  const advancedSections = getVisibleSettingsSections(CreateJobState.software, CreateJobState.pipelineType, true);

  return (
    <div>
      <div style={{ marginTop: "8px", marginBottom: "8px" }}>
        <Button
          onClick={() => onAdvancedSettingsExpandedChange(!isAdvancedSettingsExpanded)}
          aria-expanded={isAdvancedSettingsExpanded}
          fillMode="flat"
          themeColor="primary"
          style={{ paddingLeft: 0, fontWeight: "bold", fontSize: "1.1em", display: "flex", alignItems: "center" }}
        >
          <SvgIcon icon={isAdvancedSettingsExpanded ? svgIcons.chevronDownIcon : svgIcons.chevronRightIcon} />
          Advanced Settings
        </Button>
      </div>

      {isAdvancedSettingsExpanded && (
        <div>
          <fieldset className={"k-form-fieldset"}>
            <legend className={"k-form-legend"}>
              <strong>Merge Profile</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.gridLayoutIcon} />
            </legend>
            <div style={{ marginBottom: "10px" }}>
              <DropDownList
                style={{ width: "100%" }}
                data={mergeNodePoolItems}
                textField="title"
                dataItemKey="value"
                value={selectedMergeNodePool}
                loading={mergeNodePoolOptionsLoading}
                disabled={mergeNodePoolOptionsLoading || mergeNodePoolOptions.length === 0}
                valueRender={(_, value) => <span>{value?.title ?? "Use default merge pool"}</span>}
                itemRender={(li, itemProperties) => {
                  const item = itemProperties.dataItem as MergeNodePoolDropdownItem;
                  return React.cloneElement(
                    li,
                    li.props,
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "2px 0" }}>
                      <span style={{ fontWeight: 600 }}>{item.title}</span>
                      {item.summary && <span style={{ fontSize: "0.85em", color: "#64748b", whiteSpace: "normal" }}>{item.summary}</span>}
                    </div>,
                  );
                }}
                onChange={(event) => setMergeNodePoolOverride(event.value?.value ?? null)}
              />
            </div>
            {selectedMergeNodePoolOption && (
              <div style={{ marginBottom: "10px", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", backgroundColor: "#f8fafc" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px", color: "#0f172a" }}>{selectedMergeNodePoolOption.profile}</div>
                <div style={{ marginBottom: "8px", fontSize: "0.9em", color: "#64748b" }}>Node pool: {selectedMergeNodePoolOption.nodePoolName}</div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px", backgroundColor: "#ffffff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                    <strong>{selectedMergeNodePoolOption.profile}</strong>
                    {selectedMergeNodePoolOption.useSNECombine && <span style={{ color: "#166534", fontWeight: 600 }}>Uses SNE Combine</span>}
                  </div>
                  <div style={{ fontSize: "0.9em", color: "#475569", display: "flex", flexWrap: "wrap", gap: "12px" }}>
                    <span>CPU: {selectedMergeNodePoolOption.cpu}</span>
                    <span>Memory: {selectedMergeNodePoolOption.memory}</span>
                    {selectedMergeNodePoolOption.storageBuffer && <span>Storage: {selectedMergeNodePoolOption.storageBuffer}</span>}
                  </div>
                </div>
              </div>
            )}
            {CreateJobState.mergeNodePoolOverride && !selectedMergeNodePoolOption && (
              <div style={{ marginBottom: "10px", border: "1px solid #f59e0b", borderRadius: "8px", padding: "12px", backgroundColor: "#fffbeb", fontSize: "0.9em", color: "#92400e" }}>
                Legacy node pool override preserved: {CreateJobState.mergeNodePoolOverride}. Select a merge profile to convert it to the new profile-based override.
              </div>
            )}
            {CreateJobState.mergeNodePoolOverride === null && mergeNodePoolOptions.length > 0 && (
              <div style={{ marginBottom: "10px", fontSize: "0.9em", color: "#64748b" }}>Select a merge profile to view its specs, node pool, and SNE Combine usage.</div>
            )}
            {mergeNodePoolOptionsError && <div style={{ color: "#b91c1c", fontSize: "0.9em" }}>{mergeNodePoolOptionsError}</div>}
          </fieldset>

          {advancedSections.map((section) => (
            <SettingsSection key={section.settingsType} section={section} isAdvanced />
          ))}
        </div>
      )}
    </div>
  );
};
